import React, { useState, useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { I18n } from '../../helpers/i18n';
import { verifyForgotPasswordCode, sendForgotPasswordCode } from 'redux/actions/user.action';
import warning from 'static/images/mvp/warning.png';
import arrowBackIcon from 'static/images/mvp/black-arrow-left.png';
import config from '../../config';
import './signin.scss';
import {
  Button,
  FormField,
  FormSection,
  Input,
  InputLabel,
  SectionBody,
  SectionFooter,
  SectionHeader,
} from 'aws-amplify-react/lib/Amplify-UI/Amplify-UI-Components-React';
import { formTheme } from './ThemeSignIn';

const ForgotPasswordConfirm = ({ verifyForgotPasswordCode, sendForgotPasswordCode, loading, location, history, header }) => {
  const [email, setEmail] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [time, setTime] = useState(config.env === 'development' ? 5 : 120);
  const [resendLoading, setResendLoading] = useState(false);
  const [isOtpComplete, setIsOtpComplete] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    // Set mounted flag to true when component mounts
    isMountedRef.current = true;
    return () => {
      // Set mounted flag to false when component unmounts
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const emailParam = urlParams.get('email');
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    } else {
      // Redirect to forgot password if no email
      history.push('/forgot-password');
    }
  }, [location.search, history]);

  useEffect(() => {
    if (time > 0) {
      const timer = setTimeout(() => {
        if (isMountedRef.current) {
          setTime(time - 1);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [time]);

  const checkOtpComplete = () => {
    const inputs = document.querySelectorAll('.verification-container input');
    let otpLength = 0;
    inputs.forEach((input) => {
      if (input.value.trim()) {
        otpLength++;
      }
    });
    const isComplete = otpLength === 5;
    setIsOtpComplete(isComplete);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Backspace' && !event.target.value) {
      const inputs = document.querySelectorAll('.verification-container input');
      const index = Array.prototype.indexOf.call(inputs, event.target);
      
      // Clear error when user starts deleting
      if (error) {
        setError('');
      }
      
      // Clear the previous input and update state
      if (index > 0) {
        const newOtpDigits = [...otpDigits];
        newOtpDigits[index - 1] = '';
        setOtpDigits(newOtpDigits);
        inputs[index - 1].focus();
        inputs[index - 1].value = '';
      }
    }
    moveFocus('prev', event);
    // Check OTP completion after a short delay to allow the value to be cleared
    setTimeout(() => {
      if (isMountedRef.current) {
        checkOtpComplete();
      }
    }, 0);
  };

  const handleInput = (event) => {
    if (!/^\d$/.test(event.target.value)) {
      event.target.value = '';
      checkOtpComplete();
      return;
    }
    
    // Clear error when user starts typing again
    if (error) {
      setError('');
    }
    
    const inputs = document.querySelectorAll('.verification-container input');
    const index = Array.prototype.indexOf.call(inputs, event.target);
    
    // Update the OTP digits state
    const newOtpDigits = [...otpDigits];
    newOtpDigits[index] = event.target.value;
    setOtpDigits(newOtpDigits);
    
    moveFocus('next', event);
    event.target.style.animation = 'none';
    event.target.offsetHeight; /* trigger reflow */
    event.target.style.animation = null;
    checkOtpComplete();
  };

  const moveFocus = (direction, event = null) => {
    const inputs = document.querySelectorAll('.verification-container input');
    const index = Array.prototype.indexOf.call(inputs, event.target);

    if (direction === 'next' && event.target.value.length === 1) {
      if (index < inputs.length - 1) {
        inputs[index + 1].focus();
      }
    }

    if (direction === 'prev' && event.key === 'Backspace' && !event.target.value) {
      if (index > 0) {
        inputs[index - 1].focus();
      }
    }
  };

  const handleVerifyCode = async () => {
    // Only allow verification if OTP is complete and not loading
    if (!isOtpComplete || loading || isProcessing) {
      return;
    }

    const verificationCode = otpDigits.join('');
    
    if (!verificationCode || verificationCode.length !== 5) {
      setError(I18n.t('forgot_password_invalid_code'));
      return;
    }

    setError(''); // Clear error before verification

    // Development bypass: allow 12345 in development environment
    if (config.env === 'development' && verificationCode === '12345') {
      setError('');
      history.push(`/reset-password?email=${encodeURIComponent(email)}`);
      return;
    }

    setIsProcessing(true);
    try {
      await verifyForgotPasswordCode(email, verificationCode);
      // Only navigate if verification is successful and component is still mounted
      if (isMountedRef.current) {
        history.push(`/reset-password?email=${encodeURIComponent(email)}`);
      }
    } catch (err) {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setIsProcessing(false); // Reset processing state on error
        if (err.message.includes('400.59') || err.message.includes('invalid')) {
          setError(I18n.t('forgot_password_invalid_code'));
        } else {
          setError(err.message || I18n.t('something_wrong'));
        }
      }
    }
  };

  const handleResendCode = async () => {
    if (resendLoading) return; // Prevent multiple clicks
    
    setError('');
    setResendLoading(true);
    try {
      console.log('🔄 [DEBUG] Resending OTP to email:', email);
      await sendForgotPasswordCode(email);
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setTime(config.env === 'development' ? 5 : 120);
        setSuccess(I18n.t('forgot_password_verification_sent'));
        console.log('✅ [DEBUG] OTP resent successfully');
      }
    } catch (err) {
      console.error('❌ [DEBUG] Failed to resend OTP:', err);
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setError(err.message || I18n.t('something_wrong'));
      }
    } finally {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setResendLoading(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      const verificationCode = otpDigits.join('');
      if (verificationCode.length === 5) {
        handleVerifyCode();
      }
    }
  };

  const handleBack = () => {
    history.push('/forgot-password');
  };

  if (!email) {
    return <div>Loading...</div>;
  }

  const theme = {
    input: {
      color: '#000000',
      fontSize: '16px',
      lineHeight: '24px',
      borderColor: error ? '#FF445D' : '#CCCCCC',
      borderRadius: '4px',
      padding: '14px 16px',
    },
  };

  return (
    <div id="site_wrapper">
      {header}
      <FormSection theme={formTheme} style={{ width: 460 }}>
        <div className="d-flex align-items-center mb-4">
          <img 
            className="cursor-pointer" 
            src={arrowBackIcon} 
            alt="Arrow Back" 
            onClick={handleBack}
            style={{ cursor: 'pointer', marginRight: '12px' }}
          />
          <span className="title" style={{ fontSize: '24px', fontWeight: '400' }}>
            {I18n.t('forgot_password_enter_code')}
          </span>
        </div>
        <SectionBody style={{ marginBottom: 24 }}>
          {config.env === 'development' && (
            <div className="dev-hint" style={{ marginBottom: '25px', fontSize: '14px', color: '#01ECC3', background: 'rgba(1, 236, 195, 0.1)', padding: '8px 12px', borderRadius: '4px', border: '1px solid rgba(1, 236, 195, 0.3)' }}>
              <strong>Dev Mode:</strong> Use code <strong>12345</strong> to bypass verification
            </div>
          )}
          
          <div className="d-flex flex-column">
            <span className="text-muted">{I18n.t('sent_to_email')}</span>
            <p style={{ textDecoration: 'underline' }}>{email}</p>
            <div className="verification-container" style={{ display: 'flex', gap: '12px' }}>
              <input 
                type="text" 
                maxLength="1" 
                onInput={handleInput} 
                onKeyDown={handleKeyDown}
                disabled={isProcessing || loading}
                style={{
                  width: '56px',
                  height: '56px',
                  fontSize: '24px',
                  lineHeight: '36px',
                  textAlign: 'center',
                  border: '1px solid #EEEFF2',
                  borderRadius: '12px',
                  outline: 'none',
                  userSelect: 'none',
                  opacity: (isProcessing || loading) ? 0.6 : 1,
                  cursor: (isProcessing || loading) ? 'not-allowed' : 'text'
                }}
                onFocus={(e) => {
                  if (!isProcessing && !loading) {
                    e.target.style.borderColor = '#E87623';
                  }
                }}
                onBlur={(e) => e.target.style.borderColor = '#EEEFF2'}
              />
              <input 
                type="text" 
                maxLength="1" 
                onInput={handleInput} 
                onKeyDown={handleKeyDown}
                disabled={isProcessing || loading}
                style={{
                  width: '56px',
                  height: '56px',
                  fontSize: '24px',
                  lineHeight: '36px',
                  textAlign: 'center',
                  border: '1px solid #EEEFF2',
                  borderRadius: '12px',
                  outline: 'none',
                  userSelect: 'none',
                  opacity: (isProcessing || loading) ? 0.6 : 1,
                  cursor: (isProcessing || loading) ? 'not-allowed' : 'text'
                }}
                onFocus={(e) => {
                  if (!isProcessing && !loading) {
                    e.target.style.borderColor = '#E87623';
                  }
                }}
                onBlur={(e) => e.target.style.borderColor = '#EEEFF2'}
              />
              <input 
                type="text" 
                maxLength="1" 
                onInput={handleInput} 
                onKeyDown={handleKeyDown}
                disabled={isProcessing || loading}
                style={{
                  width: '56px',
                  height: '56px',
                  fontSize: '24px',
                  lineHeight: '36px',
                  textAlign: 'center',
                  border: '1px solid #EEEFF2',
                  borderRadius: '12px',
                  outline: 'none',
                  userSelect: 'none',
                  opacity: (isProcessing || loading) ? 0.6 : 1,
                  cursor: (isProcessing || loading) ? 'not-allowed' : 'text'
                }}
                onFocus={(e) => {
                  if (!isProcessing && !loading) {
                    e.target.style.borderColor = '#E87623';
                  }
                }}
                onBlur={(e) => e.target.style.borderColor = '#EEEFF2'}
              />
              <input 
                type="text" 
                maxLength="1" 
                onInput={handleInput} 
                onKeyDown={handleKeyDown}
                disabled={isProcessing || loading}
                style={{
                  width: '56px',
                  height: '56px',
                  fontSize: '24px',
                  lineHeight: '36px',
                  textAlign: 'center',
                  border: '1px solid #EEEFF2',
                  borderRadius: '12px',
                  outline: 'none',
                  userSelect: 'none',
                  opacity: (isProcessing || loading) ? 0.6 : 1,
                  cursor: (isProcessing || loading) ? 'not-allowed' : 'text'
                }}
                onFocus={(e) => {
                  if (!isProcessing && !loading) {
                    e.target.style.borderColor = '#E87623';
                  }
                }}
                onBlur={(e) => e.target.style.borderColor = '#EEEFF2'}
              />
              <input 
                type="text" 
                maxLength="1" 
                onInput={handleInput} 
                onKeyDown={handleKeyDown}
                disabled={isProcessing || loading}
                style={{
                  width: '56px',
                  height: '56px',
                  fontSize: '24px',
                  lineHeight: '36px',
                  textAlign: 'center',
                  border: '1px solid #EEEFF2',
                  borderRadius: '12px',
                  outline: 'none',
                  userSelect: 'none',
                  opacity: (isProcessing || loading) ? 0.6 : 1,
                  cursor: (isProcessing || loading) ? 'not-allowed' : 'text'
                }}
                onFocus={(e) => {
                  if (!isProcessing && !loading) {
                    e.target.style.borderColor = '#E87623';
                  }
                }}
                onBlur={(e) => e.target.style.borderColor = '#EEEFF2'}
              />
            </div>
            {error && (
              <span className="error-message mt-1">
                <img src={warning} style={{ marginTop: '-5px', marginRight: '4px' }} />
                {error}
              </span>
            )}
            {success && (
              <span className="success-message mt-1" style={{ color: '#28a745' }}>
                {success}
              </span>
            )}
            {time > 0 && (
              <div className="d-flex gap-2 mt-2">
                <span className="text-muted mr-2">{I18n.t('resend_verification_code_after')} </span>
                <p>{time}s</p>
              </div>
            )}
            {time === 0 && (
              <div className="d-flex gap-2 mt-2">
                <span
                  onClick={handleResendCode}
                  style={{ 
                    color: resendLoading ? '#999' : '#0564F1', 
                    textDecoration: 'underline', 
                    cursor: resendLoading ? 'not-allowed' : 'pointer',
                    opacity: resendLoading ? 0.6 : 1
                  }}
                >
                  {resendLoading ? I18n.t('loading') : I18n.t('resend_verification_code')}
                </span>
              </div>
            )}
          </div>
        </SectionBody>
        <SectionFooter theme={formTheme}>
          <Button 
            theme={formTheme} 
            onClick={handleVerifyCode} 
            disabled={!isOtpComplete || loading || isProcessing}
            className={(!isOtpComplete || loading || isProcessing) ? 'disabled-verify-button' : ''}
            style={(() => {
              const isDisabled = !isOtpComplete || loading || isProcessing;
              const baseStyle = {
                opacity: 1,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
              };
              if (isDisabled) {
                baseStyle.backgroundColor = '#CCCCCC';
                baseStyle.color = '#FFFFFF';
                baseStyle.border = 'none';
              }
              return baseStyle;
            })()}
          >
            {isProcessing ? I18n.t('processing') : (loading ? I18n.t('loading') : I18n.t('forgot_password_verify_code'))}
          </Button>
        </SectionFooter>
      </FormSection>
    </div>
  );
};

const mapStateToProps = ({ user }) => ({
  loading: user.loading,
});

const mapDispatchToProps = {
  verifyForgotPasswordCode,
  sendForgotPasswordCode,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ForgotPasswordConfirm));
